<template>
  <section class="page" data-testid="page-learning">
    <header class="page-header">
      <div><h1>学习</h1><p>配置持续学习监听目标，查看缓冲与历史。</p></div>
      <div class="actions">
        <button class="button" :disabled="!progressRows.length" @click="copyCurrentView">复制当前视图</button>
        <button class="button primary" :disabled="loading" @click="() => load()">{{ loading ? "刷新中" : "刷新" }}</button>
      </div>
    </header>
    <div class="metrics-grid">
      <MetricCard label="人设" :value="summary.summary?.profile_count || 0" />
      <MetricCard label="监听目标" :value="String(rows.length)" />
      <MetricCard label="待入 RAG" :value="summary.summary?.pending_rag_messages || 0" />
      <MetricCard label="已学习" :value="summary.summary?.total_learned || 0" />
      <MetricCard label="停滞" :value="summary.summary?.stale_profiles || 0" />
      <MetricCard label="暂停" :value="pausedProfiles" />
    </div>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <p v-if="error" class="error-text">{{ error }}</p>
    <div class="split">
      <div class="panel">
        <h2>监听目标</h2>
        <div class="toolbar">
          <button class="button" :class="{ primary: targetFilter === 'all' }" @click="targetFilter = 'all'">全部</button>
          <button class="button" :class="{ primary: targetFilter === 'enabled' }" @click="targetFilter = 'enabled'">开启</button>
          <button class="button" :class="{ primary: targetFilter === 'paused' }" @click="targetFilter = 'paused'">暂停</button>
        </div>
        <DataTable :columns="columns" :rows="filteredRows" row-key="key">
          <template #actions="{ row }">
            <div class="row-actions">
              <button class="button small" @click="copyJson(row, '监听目标已复制')">复制</button>
              <button class="button small danger" :disabled="loading" @click="unwatch(row.qq_id, row.persona)">移除</button>
            </div>
          </template>
        </DataTable>
      </div>
      <div>
        <div class="panel">
          <h2>添加监听</h2>
          <div class="form-grid">
            <FormField label="QQ"><input v-model="form.qq_id" /></FormField>
            <FormField label="人设">
              <select v-model="watchPersonaSelection" @change="applyWatchPersonaSelection">
                <option value="">默认人设</option>
                <option v-for="name in personas" :key="name" :value="name">{{ name }}</option>
                <option value="__custom">自定义</option>
              </select>
            </FormField>
            <FormField v-if="watchPersonaSelection === '__custom'" label="自定义人设"><input v-model="form.persona" /></FormField>
            <FormField label="群"><input v-model="form.group_id" /></FormField>
          </div>
          <button class="button primary" :disabled="loading || !form.qq_id.trim()" @click="watch">添加</button>
          <div class="material-advice" :class="watchQuality.level">
            <strong>{{ watchQuality.title }}</strong>
            <span>{{ watchQuality.detail }}</span>
          </div>
        </div>
        <div class="panel">
          <h2>学习进度</h2>
          <DataTable :columns="progressColumns" :rows="progressRows" row-key="persona">
            <template #enabled="{ row }">{{ row.enabled === false ? "暂停" : "开启" }}</template>
            <template #qq_ids="{ row }">{{ (row.qq_ids || []).join(", ") || "-" }}</template>
            <template #progress="{ row }">
              RAG {{ row.progress?.messages_until_rag ?? "-" }} / 表达 {{ row.progress?.messages_until_expression ?? "-" }} / 风格 {{ row.progress?.messages_until_style_update ?? "-" }}
            </template>
            <template #actions="{ row }">
              <div class="row-actions">
                <button class="button small" @click="selectProfile(row)">详情</button>
                <button class="button small" @click="copyJson(row, '学习进度已复制')">复制</button>
                <button v-if="row.enabled === false" class="button small" :disabled="loading" @click="resumePersona(row.persona)">恢复</button>
                <button v-else class="button small" :disabled="loading" @click="pausePersona(row.persona)">暂停</button>
              </div>
            </template>
          </DataTable>
        </div>
        <div class="panel">
          <h2>学习诊断</h2>
          <div class="diagnostic-list">
            <div v-for="item in diagnostics" :key="item.label" class="diagnostic-row" :class="item.level">
              <strong>{{ item.label }}</strong>
              <span>{{ item.detail }}</span>
            </div>
          </div>
        </div>
        <div class="panel">
          <h2>进度详情</h2>
          <div v-if="selectedProfile" class="detail-panel">
            <div class="runtime-grid compact">
              <div><span>人设</span><strong>{{ selectedProfile.persona }}</strong></div>
              <div><span>状态</span><strong>{{ selectedProfile.enabled === false ? "暂停" : "开启" }}</strong></div>
              <div><span>缓冲</span><strong>{{ selectedProfile.buffer_size || 0 }}</strong></div>
              <div><span>已学习</span><strong>{{ selectedProfile.total_learned || 0 }}</strong></div>
            </div>
            <pre>{{ JSON.stringify(selectedProfile, null, 2) }}</pre>
          </div>
          <div v-else class="empty">选择一个学习进度查看完整状态。</div>
        </div>
        <div class="panel">
          <h2>历史</h2>
          <div class="toolbar">
            <select v-model="historyPersonaSelection" @change="applyHistoryPersonaSelection">
              <option value="">自动选择</option>
              <option v-for="name in personas" :key="name" :value="name">{{ name }}</option>
              <option value="__custom">自定义</option>
            </select>
            <input v-if="historyPersonaSelection === '__custom'" v-model="historyPersona" placeholder="自定义人设" />
            <select v-model.number="historyLimit">
              <option :value="20">最近 20 条</option>
              <option :value="50">最近 50 条</option>
              <option :value="100">最近 100 条</option>
            </select>
            <button class="button" :disabled="loading" @click="loadHistory">读取历史</button>
            <button class="button" :disabled="!history.length" @click="copyHistory">复制历史</button>
          </div>
          <div class="detail-strip">
            <span>样本 {{ historySummary.returned || 0 }}</span>
            <span>发送者 {{ historySummary.unique_senders || 0 }}</span>
            <span>群 {{ historySummary.group_count || 0 }}</span>
            <span>平均长度 {{ historySummary.avg_content_length || 0 }}</span>
          </div>
          <DataTable :columns="historyColumns" :rows="history" row-key="key">
            <template #timestamp="{ row }">{{ formatTime(row.timestamp) }}</template>
            <template #source="{ row }">{{ row.sender_name || row.sender_qq || "-" }} / {{ row.group_id || "-" }}</template>
            <template #content_preview="{ row }"><span class="history-content">{{ row.content_preview || row.content || "-" }}</span></template>
          </DataTable>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson } from "../api/client";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const targets = ref<Record<string, any>>({});
const summary = ref<Record<string, any>>({ summary: {}, profiles: [] });
const history = ref<any[]>([]);
const historySummary = ref<Record<string, any>>({});
const personas = ref<string[]>([]);
const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择学习数据");
const selectedProfile = ref<Record<string, any> | null>(null);
const historyPersona = ref("");
const historyPersonaSelection = ref("");
const historyLimit = ref(20);
const form = reactive({ qq_id: "", persona: "", group_id: "" });
const watchPersonaSelection = ref("");
const targetFilter = ref<"all" | "enabled" | "paused">("all");
const columns = [
  { key: "persona", label: "人设" },
  { key: "qq_id", label: "QQ" },
  { key: "group_id", label: "群" },
  { key: "enabled", label: "状态" },
  { key: "actions", label: "操作" }
];
const progressColumns = [
  { key: "persona", label: "人设" },
  { key: "enabled", label: "状态" },
  { key: "qq_ids", label: "QQ" },
  { key: "total_learned", label: "已学" },
  { key: "buffer_size", label: "缓冲" },
  { key: "total_rag_chunks", label: "RAG" },
  { key: "total_expression_batches", label: "表达" },
  { key: "total_style_updates", label: "风格" },
  { key: "progress", label: "还差" },
  { key: "actions", label: "操作" }
];
const historyColumns = [
  { key: "timestamp", label: "时间" },
  { key: "source", label: "来源" },
  { key: "content_preview", label: "内容" },
  { key: "content_length", label: "字数" },
  { key: "context_count", label: "上下文" }
];
const rows = computed(() => Object.entries(targets.value).flatMap(([persona, value]) => {
  if (Array.isArray(value)) return value.map((qq_id) => ({ key: `${persona}:${qq_id}`, persona, qq_id, group_id: "" }));
  const qqIds = Array.isArray(value?.qq_ids) ? value.qq_ids : [];
  const groups = Array.isArray(value?.groups) ? value.groups : [];
  return qqIds.map((qq_id: string | number) => ({
    key: `${persona}:${qq_id}`,
    persona,
    qq_id,
    group_id: groups.join(", "),
    enabled: value?.enabled === false ? "暂停" : "开启"
  }));
}));
const filteredRows = computed(() => {
  if (targetFilter.value === "enabled") return rows.value.filter((row) => row.enabled !== "暂停");
  if (targetFilter.value === "paused") return rows.value.filter((row) => row.enabled === "暂停");
  return rows.value;
});
const progressRows = computed(() => summary.value.profiles || []);
const pausedProfiles = computed(() => progressRows.value.filter((row: Record<string, any>) => row.enabled === false).length);
const watchQuality = computed(() => {
  if (!form.qq_id.trim()) return { level: "warning", title: "需要 QQ", detail: "添加监听前先填入目标 QQ 号。" };
  if (!/^\d+$/.test(form.qq_id.trim())) return { level: "warning", title: "QQ 格式异常", detail: "QQ 号需要是纯数字，提交前请确认。" };
  if (!form.persona && !watchPersonaSelection.value) return { level: "warning", title: "使用默认人设", detail: "未选择人设时会写入后端默认人设。" };
  return { level: "ok", title: "监听目标可添加", detail: "QQ、人设和群范围已经可以提交。" };
});
const diagnostics = computed(() => {
  const pending = Number(summary.value.summary?.pending_rag_messages || 0);
  const stale = Number(summary.value.summary?.stale_profiles || 0);
  const targetCount = rows.value.length;
  return [
    {
      label: "学习覆盖",
      detail: targetCount ? `${targetCount} 个 QQ 目标正在被学习系统跟踪` : "暂无监听目标，机器人不会从群友发言中积累新素材",
      level: targetCount ? "ok" : "warning"
    },
    {
      label: "RAG 缓冲",
      detail: pending ? `${pending} 条消息等待进入长期记忆` : "当前没有待入 RAG 的学习消息",
      level: pending ? "warning" : "ok"
    },
    {
      label: "停滞画像",
      detail: stale ? `${stale} 个人设较久没有学习更新` : "学习人设没有明显停滞",
      level: stale ? "warning" : "ok"
    }
  ];
});
function syncPersonaSelection(value: string, selection: { value: string }) {
  if (!value) {
    selection.value = "";
  } else if (personas.value.includes(value)) {
    selection.value = value;
  } else {
    selection.value = "__custom";
  }
}
function applyWatchPersonaSelection() {
  if (watchPersonaSelection.value === "__custom") return;
  form.persona = watchPersonaSelection.value;
}
function applyHistoryPersonaSelection() {
  if (historyPersonaSelection.value === "__custom") return;
  historyPersona.value = historyPersonaSelection.value;
}
function selectProfile(row: Record<string, any>) {
  selectedProfile.value = row;
  if (row.persona) {
    historyPersona.value = row.persona;
    syncPersonaSelection(historyPersona.value, historyPersonaSelection);
  }
}
async function copyCurrentView() {
  await copyJson({
    targets: filteredRows.value,
    summary: summary.value,
    diagnostics: diagnostics.value
  }, "当前学习视图已复制");
}
async function copyHistory() {
  await copyJson({ persona: historyPersona.value, summary: historySummary.value, history: history.value }, "学习历史已复制");
}
async function load(announce = true) {
  loading.value = true;
  error.value = "";
  if (announce) actionResult.value = "";
  try {
    const [targetPayload, summaryPayload, personaPayload] = await Promise.all([
      api<{ targets: Record<string, any> }>("/api/learn/targets"),
      api<Record<string, any>>("/api/learn/summary"),
      api<{ personas: string[] }>("/api/personas")
    ]);
    targets.value = targetPayload.targets || {};
    summary.value = summaryPayload;
    personas.value = personaPayload.personas || [];
    syncPersonaSelection(form.persona, watchPersonaSelection);
    syncPersonaSelection(historyPersona.value, historyPersonaSelection);
    if (selectedProfile.value && !progressRows.value.some((row: Record<string, any>) => row.persona === selectedProfile.value?.persona)) selectedProfile.value = null;
    else if (!selectedProfile.value) selectedProfile.value = progressRows.value[0] || null;
    else selectedProfile.value = progressRows.value.find((row: Record<string, any>) => row.persona === selectedProfile.value?.persona) || selectedProfile.value;
    if (announce) actionResult.value = `已加载 ${rows.value.length} 个监听目标`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "加载学习状态失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function watch() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<Record<string, any>>("/api/learn/watch", { qq_id: form.qq_id, persona: form.persona, group_id: form.group_id || 0 });
    actionResult.value = payload.message || "监听已添加";
    toast.push("监听已添加");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "添加监听失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function unwatch(qq_id: string, persona: string) {
  if (!window.confirm(`确定移除 ${persona || "默认人设"} 对 ${qq_id} 的学习监听？`)) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<Record<string, any>>("/api/learn/unwatch", { qq_id, persona });
    actionResult.value = payload.message || "监听已移除";
    toast.push("监听已移除");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "移除监听失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function pausePersona(persona: string) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/learn/pause", { persona });
    actionResult.value = `[${persona}] 学习已暂停`;
    toast.push(`[${persona}] 学习已暂停`);
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "暂停学习失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function resumePersona(persona: string) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson("/api/learn/resume", { persona });
    actionResult.value = `[${persona}] 学习已恢复`;
    toast.push(`[${persona}] 学习已恢复`);
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "恢复学习失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function loadHistory() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const params = new URLSearchParams({ limit: String(historyLimit.value || 20) });
    if (historyPersona.value) params.set("persona", historyPersona.value);
    const payload = await api<{ history: any[]; items?: any[]; summary?: Record<string, any>; persona?: string }>(`/api/learn/history?${params.toString()}`);
    history.value = (payload.items || payload.history || []).map((item, index) => ({
      ...item,
      key: `${item.timestamp || index}:${item.sender_qq || ""}:${index}`
    }));
    historySummary.value = payload.summary || {};
    if (!historyPersona.value && payload.persona) {
      historyPersona.value = payload.persona;
      syncPersonaSelection(historyPersona.value, historyPersonaSelection);
    }
    actionResult.value = `已读取 ${history.value.length} 条学习历史`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "读取学习历史失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
function formatTime(value: unknown) {
  const n = Number(value || 0);
  return n ? new Date(n * 1000).toLocaleString("zh-CN") : "-";
}
onMounted(load);
</script>

<style scoped>
.history-content {
  display: block;
  max-width: 520px;
  white-space: normal;
  line-height: 1.5;
}
</style>
